import React, { useContext } from 'react'
import {
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { PendingAccount } from 'types/accountTypes'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { AccountContext } from 'contexts/AccountContext'
import {
  formatAveragePurchase,
  formatPhoneNumber,
  formatZipCode,
  validateExpectedMonthlySales,
  validatePhoneNumber,
  validateZipCode,
  getApiErrorMessage
} from 'utils'

const PendingAccountForm = ({
  data,
  onCancel
}: {
  data: PendingAccount
  onCancel: () => void
}) => {
  const { updatePendingAccount } = useContext(AccountContext)
  const { showMessage } = useContext(SnackbarContext)

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      ...data,
      phoneNumber: formatPhoneNumber(data.phoneNumber),
      billingPhoneNumber: formatPhoneNumber(data.billingPhoneNumber),
      shippingPhoneNumber: formatPhoneNumber(data.shippingPhoneNumber || ''),
      billingZipCode: formatZipCode(data.billingZipCode),
      shippingZipCode: formatZipCode(data.shippingZipCode || ''),
      taxId: data.taxId,
      expectedMonthlySales: formatAveragePurchase(
        data.expectedMonthlySales || ''
      )
    }
  })

  const isTaxable = watch('isTaxable')
  const hasResaleLicense = watch('hasResaleLicense')

  const onSubmit = async (formData: PendingAccount) => {
    const { createdAt, updatedAt, ...cleanData } = formData

    if (cleanData.isTaxable) {
      cleanData.businessType = ''
      cleanData.expectedMonthlySales = ''
      cleanData.salesTaxAccount = ''
      cleanData.hasResaleLicense = null
      cleanData.resaleLicenseNumber = ''
    }

    try {
      const response = await updatePendingAccount(cleanData)
      if (response?.success) {
        showMessage('Account updated successfully.')
        onCancel()
      } else {
        showMessage(response?.error || 'Update failed.')
      }
    } catch (error) {
      showMessage(getApiErrorMessage(error, 'Update failed.'))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box my={3}>
        <Typography variant='h6'>Business Info</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant='subtitle2' fontWeight={600}>
            ID
          </Typography>
          <Typography variant='body1'>{data.id}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Company Name'
            fullWidth
            {...register('companyName', {
              required: 'Required',
              maxLength: 100
            })}
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant='subtitle2' fontWeight={600}>
            Email
          </Typography>
          <Typography variant='body1'>{data.email}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name='phoneNumber'
            control={control}
            rules={{
              required: 'Required',
              validate: value =>
                validatePhoneNumber(value) || 'Format: (123) 456-7890'
            }}
            render={({ field }) => (
              <TextField
                label='Phone Number'
                fullWidth
                {...field}
                onChange={e =>
                  field.onChange(formatPhoneNumber(e.target.value))
                }
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name='alternatePhoneNumber'
            control={control}
            rules={{
              validate: value =>
                !value || validatePhoneNumber(value) || 'Format: (123) 456-7890'
            }}
            render={({ field }) => (
              <TextField
                label='Alternate Phone'
                fullWidth
                {...field}
                onChange={e =>
                  field.onChange(formatPhoneNumber(e.target.value))
                }
                error={!!errors.alternatePhoneNumber}
                helperText={errors.alternatePhoneNumber?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Sales Representative'
            fullWidth
            {...register('salesRepresentative')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name='taxId'
            control={control}
            rules={{
              required: 'Required'
            }}
            render={({ field }) => (
              <TextField
                label='Tax ID'
                fullWidth
                {...field}
                error={!!errors.taxId}
                helperText={errors.taxId?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                sx={{ height: '56px', alignItems: 'center' }}
                {...register('isTaxable')}
                defaultChecked={data.isTaxable}
              />
            }
            label='Taxable'
          />
        </Grid>

        {!isTaxable && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name='businessType'
                control={control}
                rules={{ required: 'Please select a business type.' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.businessType}>
                    <InputLabel>Business Type</InputLabel>
                    <Select
                      label='Business Type'
                      {...field}
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    >
                      <MenuItem value=''>
                        <em>Select one</em>
                      </MenuItem>
                      <MenuItem value='Contractor'>Contractor</MenuItem>
                      <MenuItem value='Builder'>Builder</MenuItem>
                      <MenuItem value='Remodel'>Remodel</MenuItem>
                      <MenuItem value='Store & Reseller'>
                        Store & Reseller
                      </MenuItem>
                    </Select>
                    {errors.businessType && (
                      <Typography
                        variant='caption'
                        sx={{ mt: '3px' }}
                        color='error'
                      >
                        {errors.businessType.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name='expectedMonthlySales'
                control={control}
                rules={{
                  validate: value =>
                    validateExpectedMonthlySales(value) ||
                    'Please enter a valid number'
                }}
                render={({ field }) => (
                  <TextField
                    label='Expected Monthly Sales'
                    fullWidth
                    {...field}
                    onChange={e =>
                      field.onChange(formatAveragePurchase(e.target.value))
                    }
                    error={!!errors.expectedMonthlySales}
                    helperText={errors.expectedMonthlySales?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Sales Tax Account'
                fullWidth
                {...register('salesTaxAccount')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ height: '56px', alignItems: 'center' }}
                    {...register('hasResaleLicense')}
                    defaultChecked={data.hasResaleLicense ?? false}
                  />
                }
                label='Has Resale License'
              />
            </Grid>

            {hasResaleLicense && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Resale License Number'
                  fullWidth
                  {...register('resaleLicenseNumber', {
                    required:
                      'Resale License Number is required when checkbox is checked.'
                  })}
                  error={!!errors.resaleLicenseNumber}
                  helperText={errors.resaleLicenseNumber?.message}
                />
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12}>
          <TextField label='Notes' fullWidth {...register('notes')} />
        </Grid>
      </Grid>

      <Box my={3}>
        <Typography variant='h6'>Billing Address</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label='First Name'
            fullWidth
            {...register('billingFirstName', { required: 'Required' })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Last Name'
            fullWidth
            {...register('billingLastName', { required: 'Required' })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Address Line 1'
            fullWidth
            {...register('billingAddressLine1', { required: 'Required' })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Address Line 2'
            fullWidth
            {...register('billingAddressLine2')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='City'
            fullWidth
            {...register('billingCity', { required: 'Required' })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='State'
            fullWidth
            {...register('billingState', { required: 'Required' })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Country'
            fullWidth
            {...register('billingCountry')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name='billingZipCode'
            control={control}
            rules={{
              required: 'Required',
              validate: value =>
                validateZipCode(value) || 'Format: 12345 or 12345-6789'
            }}
            render={({ field }) => (
              <TextField
                label='Zip Code'
                fullWidth
                {...field}
                onChange={e => field.onChange(formatZipCode(e.target.value))}
                error={!!errors.billingZipCode}
                helperText={errors.billingZipCode?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name='billingPhoneNumber'
            control={control}
            rules={{
              required: 'Required',
              validate: value =>
                validatePhoneNumber(value) || 'Format: (123) 456-7890'
            }}
            render={({ field }) => (
              <TextField
                label='Phone'
                fullWidth
                {...field}
                onChange={e =>
                  field.onChange(formatPhoneNumber(e.target.value))
                }
                error={!!errors.billingPhoneNumber}
                helperText={errors.billingPhoneNumber?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Box my={3}>
        <Typography variant='h6'>Shipping Address</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label='First Name'
            fullWidth
            {...register('shippingFirstName')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Last Name'
            fullWidth
            {...register('shippingLastName')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Address Line 1'
            fullWidth
            {...register('shippingAddressLine1')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Address Line 2'
            fullWidth
            {...register('shippingAddressLine2')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label='City' fullWidth {...register('shippingCity')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label='State' fullWidth {...register('shippingState')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Country'
            fullWidth
            {...register('shippingCountry')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name='shippingZipCode'
            control={control}
            rules={{
              validate: value =>
                validateZipCode(value) || 'Format: 12345 or 12345-6789'
            }}
            render={({ field }) => (
              <TextField
                label='Zip Code'
                fullWidth
                {...field}
                onChange={e => field.onChange(formatZipCode(e.target.value))}
                error={!!errors.shippingZipCode}
                helperText={errors.shippingZipCode?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name='shippingPhoneNumber'
            control={control}
            rules={{
              validate: value =>
                validatePhoneNumber(value) || 'Format: (123) 456-7890'
            }}
            render={({ field }) => (
              <TextField
                label='Phone'
                fullWidth
                {...field}
                onChange={e =>
                  field.onChange(formatPhoneNumber(e.target.value))
                }
                error={!!errors.shippingPhoneNumber}
                helperText={errors.shippingPhoneNumber?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Box mt={4}>
        <Button variant='contained' type='submit'>
          Save
        </Button>
        <Button variant='text' onClick={onCancel} sx={{ ml: 2 }}>
          Cancel
        </Button>
      </Box>
    </form>
  )
}

export default PendingAccountForm
